/**
 * 링크 툴바 (Notion식) — 인라인 링크 전용.
 *  - 링크 hover(또는 캐럿 진입) 시 툴팁: 🌐 URL · 복사 · 편집.
 *  - "편집" → popup: 페이지/URL 입력 · 링크 제목 입력 · 링크 제거.
 *  - 선택 텍스트에 링크 추가: promptAdd()(Ctrl+K) → 편집 popup(추가 모드).
 *  - 링크 본문 클릭 → 새 탭으로 열기.
 * 기존 인라인 링크는 <a href> DOM. 편집은 DOM 직접 수정 후 onChange()로 재직렬화/커밋.
 */

import { applyLink } from "../inline-format.js";
import { neutralizeUrl } from "../utils/url.js";

const SAFE_URL = /^(https?:|mailto:|tel:|\/|#)/i;
function normalizeHref(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (SAFE_URL.test(s)) return s;
  if (/^[\w.-]+\.[a-z]{2,}(\/|$|\?|#)/i.test(s)) return "https://" + s; // 도메인만 입력 시
  return ""; // javascript: 등 위험 프로토콜 차단
}
function esc(s) {
  return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

const ICON_GLOBE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3c2.6 2.7 2.6 15.3 0 18M12 3c-2.6 2.7-2.6 15.3 0 18"/></svg>';
const ICON_COPY = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>';
const ICON_TRASH = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13"/></svg>';

export function attachLinkToolbar(editorEl, opts) {
  opts = opts || {};
  const onChange = typeof opts.onChange === "function" ? opts.onChange : () => {};
  // 인라인 링크 → 카드/임베드 블록 "전환"(editor.js 주입). canEmbed(href): 임베드 가능(YouTube/Vimeo)일 때만 임베드 버튼 노출.
  const onConvert = typeof opts.onConvert === "function" ? opts.onConvert : null;
  const canEmbed = typeof opts.canEmbed === "function" ? opts.canEmbed : () => false;
  const LK = (opts.locale && opts.locale.link) || {};

  const bar = document.createElement("div");
  bar.className = "lumir-link-toolbar";
  bar.style.position = "absolute";
  bar.style.display = "none";
  document.body.appendChild(bar);

  let mode = null; // "view" | "edit" | "add"
  let currentAnchor = null;
  let savedRange = null, savedContent = null; // add 모드용
  let hideTimer = null, focusTimer = null, focusoutTimer = null;

  function clearHideTimer() { if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; } }
  function clearEditTimers() {
    if (focusTimer) { clearTimeout(focusTimer); focusTimer = null; }
    if (focusoutTimer) { clearTimeout(focusoutTimer); focusoutTimer = null; }
  }
  function scheduleHide() { clearHideTimer(); hideTimer = setTimeout(() => { if (mode === "view") hide(); }, 260); }
  function hide() {
    bar.style.display = "none";
    bar.classList.remove("llt-editing");
    mode = null; currentAnchor = null; savedRange = null; savedContent = null;
    clearHideTimer();
    clearEditTimers();
  }

  function closestEditable(node) {
    const el = node && node.nodeType === 1 ? node : node && node.parentElement;
    return el ? el.closest(".content, td.lumir-cell") : null;
  }
  function linkUnder(node) {
    const el = node && node.nodeType === 1 ? node : node && node.parentElement;
    const a = el && el.closest ? el.closest("a") : null;
    if (!a || !editorEl.contains(a)) return null;
    // 북마크 카드/이미지 파일명 카드의 <a>는 인라인 링크가 아니므로 링크 툴바 대상에서 제외.
    if (a.closest(".lumir-bookmark, .lumir-image")) return null;
    return a;
  }
  function rectOf(x) { try { return x && x.getBoundingClientRect ? x.getBoundingClientRect() : null; } catch (_) { return null; } }
  function position(rect) {
    rect = rect || { bottom: 0, left: 0, top: 0 };
    bar.style.display = mode === "edit" || mode === "add" ? "block" : "flex";
    const bw = bar.offsetWidth || 0, bh = bar.offsetHeight || 0;
    const vw = (document.documentElement && document.documentElement.clientWidth) || window.innerWidth || 0;
    const vh = (document.documentElement && document.documentElement.clientHeight) || window.innerHeight || 0;
    let left = rect.left || 0;
    if (vw && left + bw > vw - 8) left = vw - bw - 8;
    if (left < 8) left = 8;
    let top = (rect.bottom || 0) + 6;
    if (vh && bh && top + bh > vh - 8) { const up = (rect.top || 0) - bh - 6; top = up >= 8 ? up : Math.max(8, vh - bh - 8); }
    bar.style.left = window.scrollX + left + "px";
    bar.style.top = window.scrollY + top + "px";
  }

  function showView(a) {
    mode = "view"; currentAnchor = a;
    bar.classList.remove("llt-editing");
    const href = a.getAttribute("href") || "";
    // "전환" 액션(선택): 카드는 항상, 임베드는 YouTube/Vimeo 링크일 때만 노출.
    const convertBtns = onConvert
      ? `<button type="button" class="llt-btn" data-act="card">${LK.toCard || "카드"}</button>` +
        (canEmbed(href) ? `<button type="button" class="llt-btn" data-act="embed">${LK.toEmbed || "임베드"}</button>` : "")
      : "";
    bar.innerHTML =
      `<span class="llt-fav">${ICON_GLOBE}</span>` +
      `<a class="llt-url" href="${esc(neutralizeUrl(href))}" target="_blank" rel="noopener noreferrer">${esc(href || "(빈 링크)")}</a>` +
      `<button type="button" class="llt-icon" data-act="copy" title="${LK.copy || "URL 복사"}">${ICON_COPY}</button>` +
      `<button type="button" class="llt-btn" data-act="edit">${LK.edit || "편집"}</button>` +
      convertBtns;
    position(rectOf(a));
  }
  function showEdit(a, addRect) {
    mode = a ? "edit" : "add";
    currentAnchor = a || null;
    bar.classList.add("llt-editing");
    const href = a ? (a.getAttribute("href") || "") : "";
    const title = a ? (a.textContent || "") : (window.getSelection ? window.getSelection().toString() : "");
    bar.innerHTML =
      `<div class="llt-field"><label>${LK.pageOrUrl || "페이지 또는 URL"}</label>` +
      `<input type="text" class="llt-url-input" placeholder="${LK.placeholder || "https://…"}" value="${esc(href)}" /></div>` +
      `<div class="llt-field"><label>${LK.linkTitle || "링크 제목"}</label>` +
      `<input type="text" class="llt-title-input" value="${esc(title)}" /></div>` +
      (a
        ? `<button type="button" class="llt-remove" data-act="remove"><span class="llt-tico">${ICON_TRASH}</span>${LK.remove || "링크 제거"}</button>`
        : `<button type="button" class="llt-apply" data-act="apply">${LK.apply || "적용"}</button>`);
    position(a ? rectOf(a) : addRect);
    const inp = bar.querySelector(".llt-url-input");
    if (inp) focusTimer = setTimeout(() => { try { inp.focus(); inp.select && inp.select(); } catch (_) {} }, 0);
  }

  function unwrap(a) { if (a && a.parentNode) a.replaceWith(document.createTextNode(a.textContent || "")); }
  function applyEdit() {
    if (!bar.isConnected) return; // destroy 후 지연 타이머(focusout) 발화 방어(H1)
    const urlInput = bar.querySelector(".llt-url-input");
    const titleInput = bar.querySelector(".llt-title-input");
    const href = normalizeHref(urlInput ? urlInput.value : "");
    const title = (titleInput ? titleInput.value : "").trim();
    if (mode === "edit" && currentAnchor) {
      if (!href) { unwrap(currentAnchor); onChange(); hide(); return; } // URL 비우면 링크 해제
      currentAnchor.setAttribute("href", href);
      currentAnchor.textContent = title || href;
      onChange(); hide();
    } else if (mode === "add" && savedContent && savedRange) {
      if (href) {
        const s = window.getSelection(); s.removeAllRanges(); s.addRange(savedRange);
        if (applyLink(savedContent, href)) onChange();
      }
      hide();
    }
  }

  // 선택 텍스트에 링크 추가(Ctrl+K 등 외부 트리거)
  function promptAdd() {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return false;
    const r = sel.getRangeAt(0);
    if (r.collapsed) return false;
    const content = closestEditable(r.startContainer);
    if (!content || !editorEl.contains(content)) return false;
    savedRange = r.cloneRange(); savedContent = content;
    showEdit(null, rectOf(r));
    return true;
  }

  // 링크 본문 클릭 → 새 탭
  function onEditorClick(e) {
    const a = linkUnder(e.target);
    if (!a) return;
    const h = a.getAttribute("href");
    if (h && SAFE_URL.test(h)) { e.preventDefault(); window.open(h, "_blank", "noopener,noreferrer"); }
  }
  function onEditorOver(e) {
    if (mode === "edit" || mode === "add") return;
    const a = linkUnder(e.target);
    if (a) { clearHideTimer(); if (currentAnchor !== a || mode !== "view") showView(a); }
  }
  function onEditorOut(e) {
    if (mode === "edit" || mode === "add") return;
    if (linkUnder(e.target)) scheduleHide();
  }

  bar.addEventListener("mouseenter", clearHideTimer);
  bar.addEventListener("mouseleave", () => { if (mode === "view") scheduleHide(); });
  bar.addEventListener("mousedown", (e) => { if (!e.target.closest("input")) e.preventDefault(); }); // 선택 유지
  bar.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-act]");
    if (!btn) return;
    const act = btn.dataset.act;
    if (act === "copy" && currentAnchor) {
      const h = currentAnchor.getAttribute("href") || "";
      try { if (navigator.clipboard) navigator.clipboard.writeText(h); } catch (_) {}
      btn.classList.add("copied"); setTimeout(() => btn.classList.remove("copied"), 900);
    } else if (act === "edit" && currentAnchor) { showEdit(currentAnchor); }
    else if (act === "remove" && currentAnchor) { unwrap(currentAnchor); onChange(); hide(); }
    else if (act === "apply") { applyEdit(); }
    else if ((act === "card" || act === "embed") && currentAnchor && onConvert) {
      const anchor = currentAnchor;
      hide();
      onConvert(anchor, act); // 인라인 링크 → 북마크 블록 전환(editor.js)
    }
  });
  bar.addEventListener("keydown", (e) => {
    if (e.target.classList && (e.target.classList.contains("llt-url-input") || e.target.classList.contains("llt-title-input"))) {
      if (e.key === "Enter") { e.preventDefault(); applyEdit(); }
      else if (e.key === "Escape") { e.preventDefault(); hide(); }
    }
  });
  // 편집 popup 밖으로 포커스 나가면 적용
  bar.addEventListener("focusout", () => {
    if (mode !== "edit" && mode !== "add") return;
    focusoutTimer = setTimeout(() => { if (!bar.contains(document.activeElement) && (mode === "edit" || mode === "add")) applyEdit(); }, 120);
  });

  editorEl.addEventListener("mouseover", onEditorOver);
  editorEl.addEventListener("mouseout", onEditorOut);
  editorEl.addEventListener("click", onEditorClick);
  function onSelChange() {
    if (mode === "edit" || mode === "add") return;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const a = linkUnder(sel.getRangeAt(0).startContainer);
    if (a) { if (currentAnchor !== a || mode !== "view") showView(a); }
    else if (mode === "view") scheduleHide();
  }
  document.addEventListener("selectionchange", onSelChange);

  return {
    element: bar,
    promptAdd,
    hide,
    isOpen: () => bar.style.display !== "none",
    destroy() {
      document.removeEventListener("selectionchange", onSelChange);
      editorEl.removeEventListener("mouseover", onEditorOver);
      editorEl.removeEventListener("mouseout", onEditorOut);
      editorEl.removeEventListener("click", onEditorClick);
      clearHideTimer();
      clearEditTimers();
      if (bar.parentNode) bar.parentNode.removeChild(bar);
    },
  };
}
